import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, ShieldCheck, AlertTriangle, RefreshCw, Lock, CheckCircle2,
  Activity, ChevronRight, Copy, Loader2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { shortAddress } from "@/lib/wallet";
import {
  useArcWallet, useArcBalance, useArcTier, useArcStatus, useArcContract, useArcConnect,
} from "@/context/ArcEcosystemContext";
import ArcDiscountBadge from "@/components/ArcDiscountBadge";
import { formatConnectedSince } from "@/lib/connectedSince";

// Sprint 19 — single, in-flight connect handler. Used by every connect
// surface so duplicate clicks while a MetaMask popup is open are absorbed
// into the same promise and never trigger a second prompt.
function safeConnect(connect, connecting) {
  return (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (connecting) return;
    connect().catch(() => { /* error already surfaced via connectError + toast */ });
  };
}

function fmt(n) {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function HoldingsWidget() {
  const { wallet } = useArcWallet();
  const { connect, connecting, connectError, preLaunchMessage, connectedAt } = useArcConnect();
  const contract = useArcContract();
  const status = useArcStatus();
  const { balance, refresh, lastSync, loading } = useArcBalance();
  const { current, next, progress, tiers } = useArcTier();

  const copyAddress = async () => {
    if (!wallet?.address) return;
    try {
      await navigator.clipboard.writeText(wallet.address);
      toast.success("Wallet address copied");
    } catch (_) { /* noop */ }
  };

  return (
    <section id="holdings" className="relative py-20 sm:py-24" data-testid="holdings-section">
      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">// ecosystem access layer</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2">
              Verify your <span className="glow-cyan text-[#00F0FF]">ARC holdings</span>.
            </h2>
            <p className="mt-3 text-white/65 font-mono text-sm max-w-2xl">
              Connect your wallet to read your ARC balance directly from the BEP-20 contract.
              Read-only — no signatures, no approvals, no token transfers.
            </p>
          </div>

          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em]">
            <NetworkBadge wallet={wallet} status={status} contract={contract} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <TierCard
              wallet={wallet}
              connect={connect}
              connecting={connecting}
              connectError={connectError}
              preLaunchMessage={preLaunchMessage}
              connectedAt={connectedAt}
              balance={balance}
              current={current}
              next={next}
              progress={progress}
              loading={loading}
              error={status.error}
              isPreLaunch={status.isPreLaunch}
              wrongNetwork={status.wrongNetwork}
              lastSync={lastSync}
              onRefresh={refresh}
              onSwitchNetwork={status.switchToBSC}
              onCopyAddress={copyAddress}
            />
          </div>

          <div className="lg:col-span-5">
            <TierLadder
              tiers={tiers}
              balance={balance}
              currentId={current?.id}
              unlocked={!!wallet?.address && !status.isPreLaunch && status.correctNetwork}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- subcomponents (visual-only) ---------------- */

function NetworkBadge({ wallet, status, contract }) {
  if (!wallet?.address) {
    return (
      <span className="flex items-center gap-2 text-white/40" data-testid="network-badge-disconnected">
        <span className="w-2 h-2 rounded-full bg-white/30" /> wallet · disconnected
      </span>
    );
  }
  if (status.isPreLaunch) {
    return (
      <span className="flex items-center gap-2 text-[#F59E0B]" data-testid="network-badge-prelaunch">
        <span className="w-2 h-2 rounded-full bg-[#F59E0B] pulse-dot" /> contract · pre-launch
      </span>
    );
  }
  if (status.wrongNetwork) {
    return (
      <span className="flex items-center gap-2 text-[#FF3B30]" data-testid="network-badge-wrong">
        <AlertTriangle className="w-3.5 h-3.5" /> wrong network
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 text-[#39FF14]" data-testid="network-badge-correct">
      <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-dot" /> {contract?.network_name || "BSC"}
    </span>
  );
}

function TierCard({
  wallet, connect, connecting, connectError, preLaunchMessage, connectedAt,
  balance, current, next, progress,
  loading, error, isPreLaunch, wrongNetwork,
  lastSync, onRefresh, onSwitchNetwork, onCopyAddress,
}) {
  if (!wallet?.address) {
    return (
      <div className="glass p-7 sm:p-8 relative overflow-hidden" data-testid="tier-card-disconnected">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
        <div className="flex items-center gap-3 mb-4">
          <Wallet className="w-5 h-5 text-[#39FF14]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">// no wallet connected</span>
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-bold mb-3">Connect to verify holdings</h3>
        <p className="text-white/60 font-mono text-sm mb-6 max-w-md">
          We read your ARC balance straight from the contract. We never request approvals, signatures or
          transactions — your funds stay 100% in your control.
        </p>
        <button
          onClick={safeConnect(connect, connecting)}
          disabled={connecting}
          aria-busy={connecting}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="holdings-connect-button"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span data-testid="holdings-connect-button-label">Connecting…</span>
            </>
          ) : (
            <>
              <Wallet className="w-4 h-4" />
              <span data-testid="holdings-connect-button-label">Connect Wallet</span>
            </>
          )}
        </button>

        {connectError && !connecting && (
          <div
            className="mt-3 font-mono text-xs text-[#FF3B30]"
            data-testid="holdings-connect-error"
            role="alert"
          >
            {connectError}
          </div>
        )}

        {/* V1.1 — Future-discount visual chip (disconnected state) */}
        <div className="mt-5">
          <ArcDiscountBadge variant="block" testId="arc-badge-holdings-card" />
        </div>

        <SecurityStrip />
      </div>
    );
  }

  if (wrongNetwork) {
    return (
      <div className="glass p-7 sm:p-8 border-[#FF3B30]/40" data-testid="tier-card-wrong-network">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">// wrong network</span>
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-bold mb-3">Switch to BNB Smart Chain to read your ARC balance</h3>
        <p className="text-white/65 font-mono text-sm mb-6">
          ARC is a BEP-20 token. Your wallet is on a different network — switching is a one-click MetaMask
          confirmation, not a transaction.
        </p>
        <button onClick={onSwitchNetwork} className="btn-primary inline-flex items-center gap-2" data-testid="switch-network-button">
          Switch to BNB Smart Chain
        </button>

        {/* V1.1 — Future-discount visual chip (wrong-network state) */}
        <div className="mt-5">
          <ArcDiscountBadge variant="block" testId="arc-badge-holdings-card" />
        </div>

        <SecurityStrip />
      </div>
    );
  }

  const tierColor = current?.color || "#39FF14";

  return (
    <div
      className="glass p-7 sm:p-8 relative overflow-hidden transition-colors"
      style={{ borderColor: `${tierColor}55` }}
      data-testid="tier-card-connected"
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${tierColor}, transparent)` }} />
      <div
        className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: tierColor }}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-col gap-0.5">
          <button onClick={onCopyAddress} className="flex items-center gap-2 group" data-testid="holdings-wallet-address">
            <span className="w-2 h-2 rounded-full" style={{ background: tierColor }} />
            <span className="font-mono text-xs text-white/80 group-hover:text-white">{shortAddress(wallet.address)}</span>
            <Copy className="w-3.5 h-3.5 text-white/40 group-hover:text-white/80" />
          </button>
          {(() => {
            const sinceLabel = !wrongNetwork ? formatConnectedSince(connectedAt) : null;
            return sinceLabel ? (
              <span
                className="font-mono text-[10px] tracking-[0.15em] text-white/40 leading-none pl-4"
                data-testid="holdings-connected-since"
              >
                {sinceLabel}
              </span>
            ) : null;
          })()}
        </div>
        <div className="flex items-center gap-2">
          {lastSync && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              synced {lastSync.toLocaleTimeString()}
            </span>
          )}
          <button onClick={onRefresh} disabled={loading || isPreLaunch} className="text-white/50 hover:text-[#39FF14] disabled:opacity-40" title="Refresh" data-testid="holdings-refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Your ARC balance</div>
        <AnimatePresence mode="wait">
          <motion.div
            key={`bal-${balance}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="font-display text-5xl sm:text-6xl font-black mt-1"
            style={{ color: tierColor, textShadow: `0 0 14px ${tierColor}99` }}
            data-testid="holdings-balance-value"
          >
            {isPreLaunch ? "—" : fmt(balance)}
          </motion.div>
        </AnimatePresence>
        <div className="font-mono text-xs text-white/40 mt-1">
          {isPreLaunch ? preLaunchMessage : "ARC · live read from BEP-20 contract"}
        </div>
        {error && !isPreLaunch && !wrongNetwork && (
          <div className="font-mono text-xs text-[#FF3B30] mt-2" data-testid="holdings-error">{error}</div>
        )}
      </div>

      <div className="border-t border-white/10 pt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Current tier</span>
          {next && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50" data-testid="tier-next-target">
              next · {next.name} @ {fmt(next.min_arc)} ARC
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-2xl sm:text-3xl font-bold" style={{ color: tierColor }} data-testid="tier-current-name">
              {current?.name || "—"}
            </div>
            <div className="font-mono text-xs text-white/55">{current?.tagline}</div>
          </div>
          <ShieldCheck className="w-10 h-10" style={{ color: tierColor }} />
        </div>

        {next && (
          <div className="mt-4">
            <Progress value={progress ?? 0} className="h-2 bg-white/5" data-testid="tier-progress-bar" />
            <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mt-2">
              <span>{(progress ?? 0).toFixed(1)}% to {next.name}</span>
              <span>{Math.max(0, next.min_arc - balance).toLocaleString()} ARC away</span>
            </div>
          </div>
        )}

        {!next && (
          <div className="mt-4 flex items-center gap-2 text-[#A855F7] font-mono text-xs uppercase tracking-[0.2em]" data-testid="tier-max-reached">
            <CheckCircle2 className="w-4 h-4" /> Maximum tier reached · premium status active
          </div>
        )}
      </div>

      {current?.benefits?.length > 0 && (
        <ul className="mt-6 space-y-2">
          {current.benefits.map((b) => (
            <li key={b} className="flex items-start gap-2 text-white/70 font-mono text-sm" data-testid="tier-benefit">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tierColor }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {/* V1.1 — Future-discount visual chip directly under the tier benefits */}
      <div className="mt-5">
        <ArcDiscountBadge variant="block" testId="arc-badge-holdings-card" />
      </div>

      <SecurityStrip />
    </div>
  );
}

function SecurityStrip() {
  return (
    <div className="mt-7 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">
      <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#39FF14]" /> Read-only</div>
      <div className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#00F0FF]" /> No signatures</div>
      <div className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-[#A855F7]" /> No approvals</div>
    </div>
  );
}

function TierLadder({ tiers, balance, currentId, unlocked }) {
  return (
    <div className="glass p-5 sm:p-6 h-full" data-testid="tier-ladder">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">// utility ladder</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">v1</span>
      </div>
      <ul className="space-y-3">
        {tiers.map((t, i) => {
          const reached = unlocked && balance >= t.min_arc;
          const isCurrent = currentId === t.id;
          return (
            <motion.li
              key={t.id}
              initial={{ opacity: 0, x: 8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className={`relative p-4 border transition-colors ${isCurrent ? "border-white/40" : "border-white/10"}`}
              style={isCurrent ? { boxShadow: `inset 0 0 0 1px ${t.color}55, 0 0 18px ${t.color}22` } : {}}
              data-testid={`tier-row-${t.id}`}
            >
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5" style={{ background: t.color, boxShadow: `0 0 10px ${t.color}` }} />
                  <span className="font-display font-bold tracking-wider" style={{ color: t.color }}>{t.name}</span>
                  {isCurrent && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 border" style={{ color: t.color, borderColor: `${t.color}55` }}>
                      current
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-white/70">{fmt(t.min_arc)}+ ARC</span>
                  {reached ? (
                    <CheckCircle2 className="w-4 h-4" style={{ color: t.color }} />
                  ) : (
                    <Lock className="w-4 h-4 text-white/30" />
                  )}
                </div>
              </div>
              <div className="font-mono text-[11px] text-white/55">{t.tagline}</div>
              <ul className="mt-2 space-y-1">
                {t.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 font-mono text-[11px] text-white/55">
                    <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: reached ? t.color : "#ffffff33" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
